import React, { useState } from "react";
import { MdPersonOutline, MdOutlineLocalPhone, MdOutlineDashboardCustomize } from "react-icons/md";
 import { IoShareSocialOutline, IoInformationCircleOutline, IoCloseCircleOutline } from "react-icons/io5";
import { IoIosArrowDown } from "react-icons/io";
import { GrNotes } from "react-icons/gr";
import { GoPencil } from "react-icons/go";
import { AiOutlinePlus } from "react-icons/ai";
import NewCustomAttributeModal from "./NewCustomAttributeModal";
import { Dialog, Transition } from "@headlessui/react";

export default function ContactsTab({ expandedUserDetails }) {
    const [active, setActive] = useState("about");

    // inline edits
    const [nameEdit, setNameEdit] = useState(false);
    const [first, setFirst] = useState("");
    const [last, setLast] = useState("");
    const [emailEdit, setEmailEdit] = useState(false);
    const [email, setEmail] = useState(expandedUserDetails?.user_email || "");
    const [phoneEdit, setPhoneEdit] = useState(false);
    const [phone, setPhone] = useState(expandedUserDetails?.user_mobile || "");
    const [jobEdit, setJobEdit] = useState(false);
    const [job, setJob] = useState(expandedUserDetails?.job_title || "");
    const [userIdEdit, setUserIdEdit] = useState(false);
    const [userId, setUserId] = useState(expandedUserDetails?.id || "");
    const [showUserId, setShowUserId] = useState(false);

    // add attribute
    const [attrOpen, setAttrOpen] = useState(false);
    const [attrDropdown, setAttrDropdown] = useState(false);

    const initials =
        expandedUserDetails?.profilePic ||
        String(expandedUserDetails?.user_name || "")
            .split(" ")
            .map((w) => w[0])
            .slice(0, 2)
            .join("")
            .toUpperCase();

    return (
        <div className="space-y-3">
            {/* Header card */}
            <div className="flex items-center gap-3 rounded-2xl border p-3">
                <div className="grid h-16 w-16 place-items-center rounded-lg bg-orange-500 text-xl font-semibold text-white">
                    {initials || "U"}
                </div>

                <div className="flex-1">
                    {nameEdit ? (
                        <div className="flex items-center gap-2">
                            <input className="h-10 flex-1 rounded-xl border px-3 text-sm outline-none" value={first} onChange={(e) => setFirst(e.target.value)} placeholder="First name" />
                            <input className="h-10 flex-1 rounded-xl border px-3 text-sm outline-none" value={last} onChange={(e) => setLast(e.target.value)} placeholder="Last name" />
                            <button className="h-9 rounded-xl border px-3 text-sm" onClick={() => setNameEdit(false)}>Cancel</button>
                            <button className="h-9 rounded-xl bg-[#01C37D] px-3 text-sm font-medium text-white" onClick={() => setNameEdit(false)}>Save</button>
                        </div>
                    ) : (
                        <div className="flex items-center gap-2">
                            <h3 className="text-lg font-semibold">{expandedUserDetails?.user_name || `${first} ${last}` || "—"}</h3>
                            <button className="grid h-8 w-8 place-items-center rounded-md bg-gray-100" onClick={() => setNameEdit(true)}>
                                <GoPencil />
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Tabs */}
            <div className="grid grid-cols-4 rounded-2xl border">
                <TabBtn active={active === "about"} onClick={() => setActive("about")} icon={<MdPersonOutline className="text-xl" />} />
                <TabBtn active={active === "social"} onClick={() => setActive("social")} icon={<IoShareSocialOutline className="text-xl" />} />
                <TabBtn active={active === "more"} onClick={() => setActive("more")} icon={<IoInformationCircleOutline className="text-xl" />} />
                <TabBtn active={active === "notes"} onClick={() => setActive("notes")} icon={<GrNotes className="text-xl" />} />
            </div>

            {/* Panels */}
            {active === "about" && (
                <div className="space-y-2">
                    {/* Email */}
                    <FieldRow
                        icon="mail"
                        label="Email"
                        edit={emailEdit}
                        onEdit={setEmailEdit}
                        value={<a className="text-blue-600" href="#">{email || "—"}</a>}
                        editor={
                            <InlineEditor
                                type="email"
                                value={email}
                                onChange={setEmail}
                                onCancel={() => setEmailEdit(false)}
                                onSave={() => setEmailEdit(false)}
                            />
                        }
                    />
                    {/* Phone */}
                    <FieldRow
                        icon={<MdOutlineLocalPhone className="text-gray-500" />}
                        label="Phone"
                        edit={phoneEdit}
                        onEdit={setPhoneEdit}
                        value={phone || "—"}
                        editor={
                            <InlineEditor
                                type="number"
                                value={phone}
                                onChange={setPhone}
                                onCancel={() => setPhoneEdit(false)}
                                onSave={() => setPhoneEdit(false)}
                                addon={<button className="h-9 rounded-xl border px-3 text-sm"><AiOutlinePlus /> Add</button>}
                            />
                        }
                    />
                    {/* Job */}
                    <FieldRow
                        icon="brief"
                        label="Job Title"
                        edit={jobEdit}
                        onEdit={setJobEdit}
                        value={job || "—"}
                        editor={<InlineEditor value={job} onChange={setJob} onCancel={() => setJobEdit(false)} onSave={() => setJobEdit(false)} />}
                    />

                    {/* User ID (toggled via Add Attribute) */}
                    {showUserId && (
                        <FieldRow
                            icon={<MdOutlineDashboardCustomize className="text-gray-500" />}
                            label="User ID"
                            edit={userIdEdit}
                            onEdit={setUserIdEdit}
                            value={userId || "—"}
                            editor={<InlineEditor value={userId} onChange={setUserId} onCancel={() => setUserIdEdit(false)} onSave={() => setUserIdEdit(false)} />}
                        />
                    )}

                    {/* Add Attribute */}
                    <div
                        className="mt-3 flex cursor-pointer items-center justify-between rounded-2xl border p-3"
                        onClick={() => setAttrDropdown((s) => !s)}
                    >
                        <span>Add Attribute</span>
                        <IoIosArrowDown />
                    </div>
                    {attrDropdown && (
                        <div className="w-full rounded-2xl border p-2">
                            <button
                                className="flex w-full items-center gap-2 rounded-xl px-2 py-2 text-left hover:bg-gray-50"
                                onClick={() => setAttrOpen(true)}
                            >
                                <AiOutlinePlus /> New Custom Attribute
                            </button>
                            {!showUserId && (
                                <button
                                    className="mt-1 flex w-full items-center gap-2 rounded-xl px-2 py-2 text-left hover:bg-gray-50"
                                    onClick={() => {
                                        setShowUserId(true);
                                        setUserIdEdit(true);
                                        setAttrDropdown(false);
                                    }}
                                >
                                    <MdOutlineDashboardCustomize /> User ID
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}

            {active === "social" && (
                <div className="rounded-2xl border p-3 text-sm">
                    <div>Twitter: @username</div>
                    <div>LinkedIn: linkedin.com/in/username</div>
                    <div>Facebook: facebook.com/username</div>
                </div>
            )}

            {active === "more" && (
                <div className="rounded-2xl border p-3 text-sm">
                    <div>Address: 123 Main Street, City</div>
                    <div>Date of Birth: 01/01/1990</div>
                    <div>Languages: English, Spanish</div>
                </div>
            )}

            {active === "notes" && (
                <div className="rounded-2xl border p-3 text-sm">Add your personal notes here…</div>
            )}

            {/* New Custom Attribute modal */}
            <AttrModal open={attrOpen} onClose={() => setAttrOpen(false)} />
        </div>
    );
}

/** UI bits */
function TabBtn({ active, onClick, icon }) {
    return (
        <button
            className={`
        flex items-center justify-center border-b-4 py-3
        ${active ? "border-[#01C37D] text-[#01C37D]" : "border-transparent text-gray-500"}
      `}
            onClick={onClick}
        >
            {icon}
        </button>
    );
}

function FieldRow({ icon, label, value, edit, onEdit, editor }) {
    return (
        <div className="flex items-start gap-3 rounded-2xl p-2">
            <div className="grid h-6 w-6 place-items-center text-gray-500">{typeof icon === "string" ? <span className="text-base">•</span> : icon}</div>
            <div className="w-24 shrink-0 text-xs text-gray-500">{label}</div>
            <div className="flex-1">
                {edit ? (
                    editor
                ) : (
                    <div className="flex items-center justify-between">
                        <div className="text-sm">{value}</div>
                        <button className="grid h-8 w-8 place-items-center rounded-md bg-gray-100" onClick={() => onEdit(true)}>
                            <GoPencil />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

function InlineEditor({ value, onChange, onCancel, onSave, type = "text", addon }) {
    return (
        <div className="flex items-center gap-2">
            <input
                type={type}
                className="h-10 flex-1 rounded-xl border px-3 text-sm outline-none"
                value={value}
                onChange={(e) => onChange(e.target.value)}
            />
            {addon}
            <button className="h-9 rounded-xl border px-3 text-sm" onClick={onCancel}>Cancel</button>
            <button className="h-9 rounded-xl bg-[#01C37D] px-3 text-sm font-medium text-white" onClick={onSave}>Save</button>
        </div>
    );
}

/** Headless UI wrapper showing NewCustomAttributeModal content */
function AttrModal({ open, onClose }) {
    return (
        <Transition show={open} as={React.Fragment}>
            <Dialog onClose={onClose} className="relative z-50">
                <Transition.Child as={React.Fragment} enter="ease-out duration-150" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                    <div className="fixed inset-0 bg-black/40" />
                </Transition.Child>
                <div className="fixed inset-0 grid place-items-center p-4">
                    <Transition.Child as={React.Fragment} enter="ease-out duration-150" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-100" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                        <Dialog.Panel className="w-full max-w-2xl rounded-2xl bg-white p-4 shadow-xl">
                            <div className="mb-3 flex items-center justify-between">
                                <Dialog.Title className="text-base font-semibold flex items-center gap-2">
                                    <MdOutlineDashboardCustomize className="text-xl" />
                                    New Custom Attribute
                                </Dialog.Title>
                                <button className="grid h-8 w-8 place-items-center rounded-lg border" onClick={onClose}>
                                    <IoCloseCircleOutline />
                                </button>
                            </div>
                            <NewCustomAttributeModal onClose={onClose} />
                        </Dialog.Panel>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition>
    );
}
